import { useConnectWallet } from "@web3-onboard/react";
import { useCallback, useEffect } from "react";
import Web3 from "web3";
import { IFrameEvents } from "../constants/enum/iframe";
import useIFrameEvent from "./useIFrameEvent";
import { useAppSelector } from "../app/hooks";

const useWallet = (iframeRef?: React.RefObject<HTMLIFrameElement>) => {
  const [{ wallet }, connect, disconnect] = useConnectWallet();
  const { signMsg, signature } = useAppSelector((state) => state.auth);

  const account = wallet ? wallet.accounts[0].address : "";

  const connectWallet = useCallback(async () => {
    const wallets = await connect();
    if (wallets[0] != null) {
      const web3 = new Web3(wallets[0].provider);
      (window as any).provider = web3;
    }
  }, [connect]);

  const disconnectWallet = useCallback(async () => {
    if (wallet) {
      disconnect({ label: wallet.label });
    }
  }, [wallet, disconnect]);

  const postMessageToIframe = useCallback(
    (data: { eventType: IFrameEvents; eventData: any }) => {
      if (iframeRef?.current?.contentWindow) {
        iframeRef.current.contentWindow.postMessage(data, "*");
      }
    },
    [iframeRef]
  );

  // Move the event handler logic inside useIFrameEvent
  const handleIFrameEvent = useCallback(
    (eventName: IFrameEvents) => {
      if (iframeRef && eventName === IFrameEvents.PAGE_READY) {
        postMessageToIframe({
          eventData: account,
          eventType: IFrameEvents.ACCOUNT_CHANGE,
        });

        postMessageToIframe({
          eventData: {
            signMsg,
            signature,
          },
          eventType: IFrameEvents.AUTH_SIGNED,
        });
      }
    },
    [account, iframeRef, postMessageToIframe, signMsg, signature]
  );

  // Always call useIFrameEvent
  useIFrameEvent(iframeRef, handleIFrameEvent);

  useEffect(() => {
    postMessageToIframe({
      eventData: account,
      eventType: IFrameEvents.ACCOUNT_CHANGE,
    });
  }, [account, postMessageToIframe]);

  useEffect(() => {
    postMessageToIframe({
      eventData: {
        signMsg,
        signature,
      },
      eventType: IFrameEvents.AUTH_SIGNED,
    });
  }, [postMessageToIframe, signMsg, signature]);

  return {
    wallet,
    connectWallet,
    disconnectWallet,
    account,
    isConnected: wallet ? true : false,
  };
};

export default useWallet;
